// ============================================
// COMPENSATION TYPES
// ============================================

// Minimal interfaces for joined data (avoid circular import)
interface CompensationPerson {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CompensationCompany {
  id: string;
  name: string;
  status?: string;
  ownershipStakePercentage?: number;
  createdAt: string;
  updatedAt: string;
}

// Status types
export type HoldingsCompensationStatus = 'active' | 'pending' | 'terminated' | 'on_leave';
export type HoldingsEmploymentType = 'full_time' | 'part_time' | 'contractor' | 'intern';
export type HoldingsCompensationChangeType =
  | 'initial'
  | 'raise'
  | 'promotion'
  | 'adjustment'
  | 'benefits_change'
  | 'status_change';

// Benefit line item
export interface BenefitItem {
  name: string;
  amountCents: number;
}

// Main compensation interface
export interface HoldingsCompensation {
  id: string;
  personId: string;
  companyId: string;

  // Pay structure (annual amounts in cents)
  baseSalaryCents: number;
  targetBonusCents: number;
  equityValueCents: number;
  benefitsCents: number;
  benefitsBreakdown: BenefitItem[];
  employerTaxesCents: number;
  employerTaxRate: number;
  additionalFeesCents: number;
  additionalFeesDescription?: string;
  totalAnnualCostCents: number;

  // Status and dates
  status: HoldingsCompensationStatus;
  effectiveDate: string;
  endDate?: string;

  // Employment metadata
  employmentType: HoldingsEmploymentType;
  title?: string;
  department?: string;
  notes?: string;

  // Audit
  createdBy?: string;
  createdAt: string;
  updatedAt: string;

  // Joined data
  person?: CompensationPerson;
  company?: CompensationCompany;
  history?: HoldingsCompensationHistory[];
}

// Compensation history for tracking changes
export interface HoldingsCompensationHistory {
  id: string;
  compensationId: string;
  baseSalaryCents: number;
  targetBonusCents?: number;
  equityValueCents?: number;
  benefitsCents?: number;
  benefitsBreakdown?: BenefitItem[];
  employerTaxesCents?: number;
  additionalFeesCents?: number;
  totalAnnualCostCents: number;
  effectiveDate: string;
  changeReason?: string;
  changeType?: HoldingsCompensationChangeType;
  changedBy?: string;
  createdAt: string;
}

// Display configuration
export const COMPENSATION_STATUSES: {
  status: HoldingsCompensationStatus;
  label: string;
  color: string;
}[] = [
  { status: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  { status: 'pending', label: 'Pending Start', color: 'bg-amber-100 text-amber-700' },
  { status: 'terminated', label: 'Terminated', color: 'bg-red-100 text-red-700' },
  { status: 'on_leave', label: 'On Leave', color: 'bg-blue-100 text-blue-700' },
];

export const EMPLOYMENT_TYPES: {
  type: HoldingsEmploymentType;
  label: string;
}[] = [
  { type: 'full_time', label: 'Full-Time' },
  { type: 'part_time', label: 'Part-Time' },
  { type: 'contractor', label: 'Contractor' },
  { type: 'intern', label: 'Intern' },
];

export const CHANGE_TYPES: {
  type: HoldingsCompensationChangeType;
  label: string;
}[] = [
  { type: 'initial', label: 'New Hire' },
  { type: 'raise', label: 'Raise' },
  { type: 'promotion', label: 'Promotion' },
  { type: 'adjustment', label: 'Adjustment' },
  { type: 'benefits_change', label: 'Benefits Change' },
  { type: 'status_change', label: 'Status Change' },
];

// Helper functions
export const getMonthlyCompensation = (comp: HoldingsCompensation): number => {
  return Math.round(comp.totalAnnualCostCents / 12);
};

export const getCompensationBreakdown = (comp: HoldingsCompensation) => {
  return {
    base: comp.baseSalaryCents,
    bonus: comp.targetBonusCents,
    equity: comp.equityValueCents,
    benefits: comp.benefitsCents,
    benefitsBreakdown: comp.benefitsBreakdown,
    taxes: comp.employerTaxesCents,
    additionalFees: comp.additionalFeesCents,
    total: comp.totalAnnualCostCents,
  };
};

// Transform benefit items from DB format
const transformBenefitsBreakdown = (data: any): BenefitItem[] => {
  if (!data || !Array.isArray(data)) return [];
  return data.map((item: any) => ({
    name: item.name || '',
    amountCents: item.amount_cents || 0,
  }));
};

// Transform API response to frontend interface
export const transformCompensation = (data: any): HoldingsCompensation => ({
  id: data.id,
  personId: data.person_id,
  companyId: data.company_id,
  baseSalaryCents: data.base_salary_cents,
  targetBonusCents: data.target_bonus_cents || 0,
  equityValueCents: data.equity_value_cents || 0,
  benefitsCents: data.benefits_cents || 0,
  benefitsBreakdown: transformBenefitsBreakdown(data.benefits_breakdown),
  employerTaxesCents: data.employer_taxes_cents || 0,
  employerTaxRate: data.employer_tax_rate || 0.0765,
  additionalFeesCents: data.additional_fees_cents || 0,
  additionalFeesDescription: data.additional_fees_description,
  totalAnnualCostCents: data.total_annual_cost_cents || 0,
  status: data.status || 'active',
  effectiveDate: data.effective_date,
  endDate: data.end_date,
  employmentType: data.employment_type,
  title: data.title,
  department: data.department,
  notes: data.notes,
  createdBy: data.created_by,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
  person: data.holdings_people ? {
    id: data.holdings_people.id,
    name: data.holdings_people.name,
    email: data.holdings_people.email,
    phone: data.holdings_people.phone,
    role: data.holdings_people.role,
    notes: data.holdings_people.notes,
    isActive: data.holdings_people.is_active,
    createdAt: data.holdings_people.created_at,
    updatedAt: data.holdings_people.updated_at,
  } : undefined,
  company: data.holdings_companies ? {
    id: data.holdings_companies.id,
    name: data.holdings_companies.name,
    status: data.holdings_companies.status,
    ownershipStakePercentage: data.holdings_companies.ownership_stake_percentage,
    createdAt: data.holdings_companies.created_at,
    updatedAt: data.holdings_companies.updated_at,
  } : undefined,
});

export const transformCompensationHistory = (data: any): HoldingsCompensationHistory => ({
  id: data.id,
  compensationId: data.compensation_id,
  baseSalaryCents: data.base_salary_cents,
  targetBonusCents: data.target_bonus_cents,
  equityValueCents: data.equity_value_cents,
  benefitsCents: data.benefits_cents,
  benefitsBreakdown: transformBenefitsBreakdown(data.benefits_breakdown),
  employerTaxesCents: data.employer_taxes_cents,
  additionalFeesCents: data.additional_fees_cents,
  totalAnnualCostCents: data.total_annual_cost_cents,
  effectiveDate: data.effective_date,
  changeReason: data.change_reason,
  changeType: data.change_type,
  changedBy: data.changed_by,
  createdAt: data.created_at,
});
